import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Edit3, LogOut, RefreshCcw, Truck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import driverService from '../services/driverService';
import DriverForm from '../components/drivers/DriverForm';
import DriverProfile from '../components/drivers/DriverProfile';

const DriverPortal = () => {
  const { userData, company, logout } = useAuth();
  const navigate = useNavigate();
  const [driverRecord, setDriverRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [mode, setMode] = useState('loading');
  const [serverError, setServerError] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');

  const companyId = useMemo(() => {
    if (company?.id) return company.id;
    if (company?.companyId) return company.companyId;
    if (userData?.companyId) return userData.companyId;
    if (userData?.company?.id) return userData.company.id;
    return null;
  }, [company, userData]);

  const normalizedEmail = userData?.email?.toLowerCase() || '';
  // Primitive deps: the auth profile object is replaced on every refresh.
  const driverLookup = useMemo(
    () => ({ uid: userData?.uid, email: userData?.email, driverId: userData?.driverId }),
    [userData?.uid, userData?.email, userData?.driverId]
  );
  const isDriver = userData?.role === 'driver';
  const BRAND_NAME = 'Himil Fleet';
  const BRAND_TAGLINE = 'Driver workspace powered by Himil Fleet';
  const BRAND_LOGO_SRC = '/Himil-logo.png';

  const initialDraft = useMemo(() => ({
    driverId: userData?.uid ? `DRV-${userData.uid.slice(-6).toUpperCase()}` : '',
    employeeId: userData?.uid ? `EMP-${userData.uid.slice(-6).toUpperCase()}` : '',
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    email: userData?.email || '',
    phone: userData?.phone || '',
    companyId
  }), [userData, companyId]);

  const fetchDriver = useCallback(async () => {
    if (!companyId || !normalizedEmail) {
      setDriverRecord(null);
      setMode('onboarding');
      setLoading(false);
      return;
    }

    setLoading(true);
    setPageError('');

    try {
      const matchedDriver = await driverService.findDriverForUser(driverLookup, { companyId });

      setDriverRecord(matchedDriver || null);
      setMode(matchedDriver ? 'view' : 'onboarding');
    } catch (error) {
      console.error('Failed to fetch driver profile:', error);
      setPageError(error.getUserFriendlyMessage?.() || 'Unable to load your driver details.');
    } finally {
      setLoading(false);
    }
  }, [companyId, normalizedEmail, driverLookup]);

  useEffect(() => {
    fetchDriver();
  }, [fetchDriver]);

  const handleDriverSubmit = async (formValues) => {
    if (!companyId) {
      const missingContextError = new Error('Company context is missing. Please contact your administrator.');
      setServerError(missingContextError.message);
      throw missingContextError;
    }

    try {
      setServerError(null);
      let savedDriver;

      if (driverRecord) {
        savedDriver = await driverService.updateDriver(driverRecord.id, formValues, {
          companyId,
          defaults: driverRecord
        });
      } else {
        savedDriver = await driverService.createDriver(formValues, { companyId });
      }

      setDriverRecord(savedDriver);
      setMode('view');
      setPageError('');
      return savedDriver;
    } catch (error) {
      console.error('Failed to save driver profile:', error);
      setServerError(error?.getUserFriendlyMessage?.() || error?.message || 'Unable to save driver profile.');
      throw error;
    }
  };

  const handleEditCancel = () => {
    setServerError(null);
    setMode(driverRecord ? 'view' : 'onboarding');
  };

  const handleLogout = useCallback(async () => {
    setLogoutError('');
    setIsLoggingOut(true);

    try {
      const result = await logout();

      if (!result?.success) {
        throw new Error(result?.error || 'Logout failed. Please try again.');
      }

      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Driver logout failed:', error);
      setLogoutError(error?.message || 'Unable to log out. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout, navigate]);

  if (!isDriver) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">This page is only available to driver accounts.</p>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold text-gray-800">Company context missing</p>
          <p className="text-sm text-gray-600">We could not determine which company you belong to. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading your driver workspace...</p>
        </div>
      </div>
    );
  }

  if (mode === 'onboarding' || mode === 'edit') {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <p className="text-sm uppercase tracking-wide text-indigo-600 font-semibold">Driver Workspace</p>
            <h1 className="text-3xl font-bold text-gray-900 mt-2">{driverRecord ? 'Update your driver profile' : 'Complete your driver profile'}</h1>
            <p className="mt-2 text-gray-600">Provide accurate information so your operations team can keep you compliant and connected to the right vehicles.</p>
          </div>

          <DriverForm
            driver={driverRecord || initialDraft}
            onSubmit={handleDriverSubmit}
            onCancel={driverRecord ? handleEditCancel : null}
            serverError={serverError}
          />
        </div>
      </div>
    );
  }

  const statusLabel = driverRecord?.status || (driverRecord?.isActive ? 'Active' : 'Inactive') || 'Unknown';

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <img src={BRAND_LOGO_SRC} alt={`${BRAND_NAME} logo`} className="h-10 w-auto mx-auto sm:mx-0" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-semibold uppercase text-indigo-600 tracking-wide">{BRAND_NAME}</p>
              <p className="text-sm text-gray-600">{BRAND_TAGLINE}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full sm:w-auto">
            <Link
              to="/driver-vehicle"
              state={{ fromPortal: true }}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 w-full sm:w-auto"
            >
              <Truck className="h-4 w-4 mr-2" />
              View Assigned Vehicle
            </Link>
            <button
              type="button"
              onClick={fetchDriver}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 w-full sm:w-auto"
            >
              <RefreshCcw className="h-4 w-4 mr-2" />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setMode('edit')}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Update Info
            </button>
            <button
              type="button"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-70 w-full sm:w-auto"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </button>
          </div>
        </div>
        {logoutError && (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {logoutError}
          </div>
        )}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-center sm:text-left">
              <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wide">Driver Workspace</p>
              <h1 className="text-3xl font-bold text-gray-900 mt-2">{driverRecord?.firstName || userData?.firstName || 'Driver'} {driverRecord?.lastName || userData?.lastName}</h1>
              <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap gap-2 text-sm text-gray-600">
                <span className="flex justify-between sm:block"><span className="sm:hidden font-medium text-gray-500">Driver ID:</span> <strong className="text-gray-900">{driverRecord?.driverId || '—'}</strong></span>
                <span className="flex justify-between sm:block"><span className="sm:hidden font-medium text-gray-500">Employee ID:</span> <strong className="text-gray-900">{driverRecord?.employeeId || '—'}</strong></span>
                <span className="flex justify-between sm:block"><span className="sm:hidden font-medium text-gray-500">Status:</span> <strong className="text-gray-900 capitalize">{statusLabel}</strong></span>
              </div>
            </div>
          </div>

          {pageError && (
            <div className="mt-4 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {pageError}
            </div>
          )}
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          {driverRecord?.id ? (
            <div className='m-5'>
              <DriverProfile
                driverId={driverRecord.id}
                initialDriver={driverRecord}
                onDriverUpdated={(updated) => updated && setDriverRecord(updated)}
                readOnly
              />
            </div>
          ) : (
            <div className="p-6 text-sm text-gray-600">
              Driver record not found. Please complete your profile.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverPortal;
